import type { FloorPath } from "building-types/floors/floorTypes";
import { isSolidFoundationAssembly } from "building-types/foundations/foundationAssemblyEntityTypes";
import type { FoundationComponentEntity } from "building-types/foundations/foundationComponentEntityTypes";
import type { FoundationCondition } from "building-types/foundations/foundationEnums";
import { BrickLedgeBehavior, BrickLedgeType } from "building-types/foundations/foundationEnums";
import type {
  FoundationPath,
  FoundationSectionSize,
  IsolatedFoundationSectionSize,
} from "building-types/foundations/foundationTypes";
import { FoundationSectionType } from "building-types/foundations/foundationTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { DoorSettings } from "building-types/settings/doorSettings";
import { PathPriority } from "building-types/walls/pathPriority";
import type { Table } from "common/data/table";
import { isExteriorRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { isPerimeterFoundationCondition, isBrickLedgeFoundationCondition } from "doc/utils/foundationSystemTools";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { defaultFoundationDatumAbstractions } from "../datums/defaultDatumAbstractions";
import type { FoundationPathEnds } from "./foundationStrip";
import {
  FoundationSectionAlignment,
  foundationPathFloorPerimeterEnds,
  foundationPathOpenings,
  linearFoundationSectionSizeFromAlignment,
  voidFoundationSectionSizeNew,
} from "./foundationStrip";

/**
 *
 * @note It seems we are duplicating the foundation volume when the brick ledge is applied!! discuss with @abrahamdrechsler and @jaggyluis to see if this is expected behavior.
 *
 */
export function makeBrickLedgeSize({
  level,
  sectionComponent,
  foundationPath,
  floorPath,
  pathEnds,
  brickLedgeType,
  brickLedgeBehavior,
  brickLedgeWidth,
  doorSettings,
  isolatedFoundationSectionSizes,
  brickLedgeDepthModifier = 0,
  brickLedgeTopMax,
  brickLedgeOffset,
  material,
  planarGraphs,
}: {
  level: Level;
  sectionComponent: FoundationComponentEntity; // pass through param
  foundationPath: FoundationPath;
  floorPath?: FloorPath;
  pathEnds: FoundationPathEnds;
  brickLedgeType: BrickLedgeType;
  brickLedgeBehavior: BrickLedgeBehavior;
  brickLedgeWidth: number;
  doorSettings: DoorSettings;
  isolatedFoundationSectionSizes: IsolatedFoundationSectionSize[];
  brickLedgeDepthModifier?: number;
  brickLedgeTopMax?: number;
  brickLedgeOffset?: number;
  material?: ExteriorMaterial;
  planarGraphs: Table<PolygonPlanarGraph>;
}): FoundationSectionSize {
  const { floorElevation, levelDatumsGenerated } = level;
  const { condition, subSystemOffset } = foundationPath;

  /**
   * @note floorPriority is important in determining the alignment of the brick ledge
   * for foundation paths derived from a horizontal assembly, we need to set the floorPriority. It can be a
   * user input and default can be LEFT. This way it works as expected for closed rooms type loops but for
   * free form user has some control if brick ledge is desired on the left or right side there. discuss with @abrahamdrechsler
   */
  const { leftRoom, rightRoom, floorPriority } = floorPath ?? {
    leftRoom: undefined,
    rightRoom: undefined,
    floorPriority: PathPriority.LEFT,
  };
  const { instanceOffset, wainscot } = levelDatumsGenerated;

  const sectionType = FoundationSectionType.LEDGE;
  const sectionId: string = sectionType;

  if (brickLedgeTypeAtCondition(condition, brickLedgeType, brickLedgeBehavior) === BrickLedgeType.NONE) {
    return voidFoundationSectionSizeNew(sectionComponent, sectionType);
  }

  const leftRoomExterior = leftRoom && isExteriorRoomEnclosure[leftRoom.attributes.roomEnclosure];
  const rightRoomExterior = rightRoom && isExteriorRoomEnclosure[rightRoom.attributes.roomEnclosure];

  const alignment =
    floorPriority === PathPriority.LEFT ? FoundationSectionAlignment.RIGHT : FoundationSectionAlignment.LEFT;
  // subSystemOffset is read directly here rather than going through foundationPathOffsetAlignment
  // (which zeroes it for CENTER-aligned paths). This is safe because freeform foundation paths
  // always have subSystemOffset = 0; brickLedgeOffset handles the CENTER case instead.
  const offset = subSystemOffset + (brickLedgeOffset ?? 0);
  const { start, end } = foundationPathFloorPerimeterEnds({
    foundationPath,
    floorPath,
    ends: pathEnds,
    planarGraphs,
  });

  const width = brickLedgeWidth;

  let top = floorElevation - instanceOffset + wainscot;
  if (brickLedgeTopMax !== undefined) {
    top = Math.min(top, brickLedgeTopMax);
  }

  if (isSolidFoundationAssembly(foundationPath.assembly)) {
    if (foundationPath.assembly?.brickLedgeTopDatumAbstraction) {
      top = foundationPath.assembly.brickLedgeTopDatumAbstraction.resolveVerticalDatum({
        level,
        path: foundationPath,
      });
    }
  }

  const datumAbstraction =
    foundationPath.assembly.bottomDatumAbstraction ?? defaultFoundationDatumAbstractions.bottomDatum;
  const bottom = datumAbstraction.resolveVerticalDatum({
    level,
    path: foundationPath,
  });

  const depth = top - bottom + brickLedgeDepthModifier;

  const gapOpeningAdjustment = (leftRoomExterior || rightRoomExterior) && floorPath ? -width : 0;
  const includeWindows = level.floorIndex < 0;
  const { openings } = foundationPathOpenings({
    foundationPath,
    floorPath,
    gapOpeningAdjustment,
    doorSettings,
    includeWindows,
    isolatedFoundationSectionSizes,
    extendGarageDoorOpeningsToBOFoundation: true,
    planarGraphs,
  });

  return linearFoundationSectionSizeFromAlignment({
    sectionComponent,
    sectionType,
    sectionId,
    top,
    depth,
    width,
    alignment,
    offset,
    start,
    end,
    openings,
    material,
  });
}

export function brickLedgeTypeAtCondition(
  foundationCondition: FoundationCondition,
  brickLedgeType: BrickLedgeType,
  brickLedgeBehavior: BrickLedgeBehavior,
) {
  // default legacy behavior
  if (brickLedgeBehavior === BrickLedgeBehavior.AUTO) {
    if (brickLedgeType === BrickLedgeType.NONE) {
      return BrickLedgeType.NONE;
    }

    if (!isPerimeterFoundationCondition[foundationCondition] && brickLedgeType === BrickLedgeType.ADD) {
      return BrickLedgeType.NONE;
    }

    if (!isBrickLedgeFoundationCondition[foundationCondition]) {
      return BrickLedgeType.NONE;
    }
  }

  return brickLedgeType;
}
